import React, { useContext, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useParams } from "react-router-dom";

import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";

import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import MuiAlert from "@mui/material/Alert";
import CloseIcon from "@mui/icons-material/Close";
import Snackbar from "@mui/material/Snackbar";

import Stack from "@mui/material/Stack";
import EventIcon from "@mui/icons-material/Event";

import Student from "components/users/Student";
import AssignmentLink from "components/elements/AssignmentLink";
import AssignmentResult from "components/users/AssignmentResult";
import AttachAssignmentForm from "components/forms/AttachAssignment";
import TheoryAssignmentForm from "components/forms/TheoryAssignment";
import OpenQuestionAssignmentForm from "components/forms/OpenQuestionAssignment";

import { ThemeContext } from "components/common/App";

import rpc, { buildRequest, signoutAndRedirectToIndex } from "lib/rpc";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

export default function LessonDetailsPage() {
  const theme = useContext(ThemeContext);

  let { groupId, lessonId } = useParams();
  const [lesson, setLesson] = useState();
  const [group, setGroup] = useState();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState("success");
  // Teacher-authored lesson topic (#123). Seeded from the fetched lesson.
  const [title, setTitle] = useState("");
  const [savingTitle, setSavingTitle] = useState(false);
  // #125 time-estimate override. Empty string = "auto" (the null override,
  // student surfaces show the content heuristic instead).
  const [estimate, setEstimate] = useState("");
  const [savingEstimate, setSavingEstimate] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    theme.changeTheme("light-bg");
    window.scrollTo(0, 0); // Make sure to be on top of the page

    rpc.post("", buildRequest("admin.groups.get", { group_id: groupId })).then((response) => {
      if (!response.data.result.message) {
        setGroup(response.data.result);
      } else {
        if (response.data.result.code === 5) {
          signoutAndRedirectToIndex();
        }
        console.error(response.data.result);
      }
    });

    fetchLesson(lessonId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setOpen(false);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const fetchLesson = (lessonId) => {
    rpc.post("", buildRequest("admin.groups.lesson_details", { lesson_id: lessonId })).then((response) => {
      if (!response.data.result.message) {
        setLesson(response.data.result);
        setTitle(response.data.result.title || "");
        const savedEstimate = response.data.result.estimated_minutes;
        setEstimate(savedEstimate == null ? "" : String(savedEstimate));
      } else {
        if (response.data.result.code === 5) {
          signoutAndRedirectToIndex();
        }
        console.error(response.data.result);
      }
    });
  };

  const detachAssignment = (lessonId, assignmentId) => {
    rpc
      .post(
        "",
        buildRequest("admin.assignment.detach", {
          lesson_id: lessonId,
          assignment_id: assignmentId,
        }),
      )
      .then((response) => {
        if (!response.data.result.message) {
          if (response.data.result.success) {
            fetchLesson(lessonId);
            setSeverity("success");
            setMessage(`Assigment [${assignmentId}] is detached from the Lesson [${lessonId}]`);
            setOpen(true);
          }
        } else {
          if (response.data.result.code === 5) {
            signoutAndRedirectToIndex();
          }
          console.error(response.data.result);
        }
      });
  };

  // Save the lesson topic (#123). An empty title clears it (students fall back
  // to "Урок {number}").
  const saveTitle = () => {
    setSavingTitle(true);
    rpc
      .post("", buildRequest("admin.groups.lesson.update", { lesson_id: lesson.id, title }))
      .then((response) => {
        const result = response.data.result;
        if (result && !result.code) {
          setLesson((prev) => ({ ...prev, title: result.title }));
          setTitle(result.title || "");
          setSeverity("success");
          setMessage("Lesson topic saved");
          setOpen(true);
        } else if (result && result.code === 5) {
          signoutAndRedirectToIndex();
        } else {
          // Save failed (e.g. code 3 — not your group). Reset the field to the
          // stored value so it doesn't show an unsaved edit, and tell the user.
          console.error(result);
          setTitle((lesson && lesson.title) || "");
          setSeverity("error");
          setMessage((result && result.message) || "Failed to save the lesson topic");
          setOpen(true);
        }
      })
      .finally(() => setSavingTitle(false));
  };

  // Save the #125 time-estimate override. An empty field clears it (null →
  // students see the content heuristic); otherwise send the integer minutes.
  const saveEstimate = () => {
    const trimmed = estimate.trim();
    const value = trimmed === "" ? null : Number(trimmed);
    if (value !== null && (!Number.isInteger(value) || value < 0)) {
      setSeverity("error");
      setMessage("Enter whole minutes (or leave empty for auto)");
      setOpen(true);
      return;
    }
    setSavingEstimate(true);
    rpc
      .post(
        "",
        buildRequest("admin.groups.lesson.update", {
          lesson_id: lesson.id,
          estimated_minutes: value,
        }),
      )
      .then((response) => {
        const result = response.data.result;
        if (result && !result.code) {
          setLesson((prev) => ({ ...prev, estimated_minutes: result.estimated_minutes }));
          setEstimate(result.estimated_minutes == null ? "" : String(result.estimated_minutes));
          setSeverity("success");
          setMessage("Time estimate saved");
          setOpen(true);
        } else if (result && result.code === 5) {
          signoutAndRedirectToIndex();
        } else {
          console.error(result);
          setEstimate(
            lesson && lesson.estimated_minutes != null ? String(lesson.estimated_minutes) : "",
          );
          setSeverity("error");
          setMessage((result && result.message) || "Failed to save the time estimate");
          setOpen(true);
        }
      })
      .finally(() => setSavingEstimate(false));
  };

  // The lesson and the group are fetched independently, so whichever answers
  // first renders alone. The results table guarded `lesson` but read
  // `group.students` straight through, and the lesson usually wins the race —
  // the page died with "Cannot read properties of undefined (reading
  // 'students')" before a teacher saw a single row. One list, guarded once.
  const students = (group && group.students) || [];

  const action = (
    <React.Fragment>
      <IconButton size="small" aria-label="close" color="inherit" onClick={handleClose}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </React.Fragment>
  );

  return (
    <>
      <Helmet>
        <title>Lesson Details {lesson && lesson.number ? `#${lesson.number}` : "loading..."}</title>
      </Helmet>
      <Snackbar
        open={open}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        autoHideDuration={2000}
        onClose={handleClose}
        action={action}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {message}
        </Alert>
      </Snackbar>

      <Container fixed sx={{ paddingBottom: 3 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Stack direction={"row"} spacing={1} divider={<Divider orientation="vertical" flexItem />}>
                <Typography variant="h4" gutterBottom>
                  Lesson #{lesson && lesson.number}
                </Typography>
                <Typography variant="h4" gutterBottom>
                  {group && group.name}
                </Typography>
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <Stack direction={"row"} spacing={1} alignItems="center">
                <TextField
                  label="Тема уроку (topic)"
                  placeholder="напр. House. How many…"
                  size="small"
                  fullWidth
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                  helperText="Показується учням замість «Урок №». Порожнє — повертає номер."
                  disabled={!lesson || savingTitle}
                />
                <Button variant="contained" onClick={saveTitle} disabled={!lesson || savingTitle}>
                  {savingTitle ? "Зберігаємо…" : "Зберегти"}
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <Stack direction={"row"} spacing={1} alignItems="center">
                <TextField
                  label="Оцінка часу, хв (time estimate)"
                  placeholder="авто"
                  type="number"
                  size="small"
                  sx={{ maxWidth: 320 }}
                  fullWidth
                  value={estimate}
                  onChange={(event) => setEstimate(event.target.value)}
                  inputProps={{ min: 0, max: 1440 }}
                  helperText="Показується учням як «~N хв». Порожнє — авто-оцінка за змістом уроку."
                  disabled={!lesson || savingEstimate}
                />
                <Button variant="contained" onClick={saveEstimate} disabled={!lesson || savingEstimate}>
                  {savingEstimate ? "Зберігаємо…" : "Зберегти"}
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="secondary" sx={{ verticalAlign: "middle" }}>
                <EventIcon fontSize="small" /> {lesson && lesson.scheduled_on}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              {group ? (
                <Paper sx={{ padding: 2 }}>
                  {students.length > 0 && (
                    <Stack direction="row" spacing={2}>
                      {students.map((student) => (
                        <Student key={`student-${student.id}`} {...student} />
                      ))}
                    </Stack>
                  )}
                </Paper>
              ) : (
                <CircularProgress />
              )}
            </Grid>

            <Grid item xs={12}>
              <Paper>
                <TableContainer>
                  <Table stickyHeader aria-label="sticky table" size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Assignment</TableCell>
                        {group ? (
                          students.map((student) => (
                            <TableCell key={`group-student-${student.id}`}>
                              <Student {...student} />
                            </TableCell>
                          ))
                        ) : (
                          <CircularProgress />
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {lesson ? (
                        lesson.assignments.map((assignment) => (
                          <TableRow key={`assignment-${assignment.id}`}>
                            <TableCell>
                              <Stack direction="row" spacing={2}>
                                <AssignmentLink {...assignment} />
                                <IconButton
                                  aria-label="delete"
                                  onClick={() => {
                                    detachAssignment(lesson.id, assignment.id);
                                  }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Stack>
                            </TableCell>
                            {students.map((student) => (
                              <TableCell key={`result-${assignment.id}__student-${student.id}`}>
                                {lesson.students_results[assignment.id] &&
                                  lesson.students_results[assignment.id][student.id] && (
                                    <AssignmentResult {...lesson.students_results[assignment.id][student.id]} />
                                  )}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))
                      ) : (
                        <CircularProgress />
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Paper sx={{ padding: 2 }}>
                <AttachAssignmentForm lessonId={lesson && lesson.id} refreshLesson={fetchLesson} />
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Paper sx={{ padding: 2 }}>
                <Tabs value={activeTab} onChange={handleTabChange} aria-label="basic tabs example">
                  <Tab label="Theory" {...a11yProps(0)} />
                  <Tab label="OpenQuestion" {...a11yProps(1)} />
                </Tabs>
                <TabPanel value={activeTab} index={0}>
                  <TheoryAssignmentForm lessonId={lesson && lesson.id} refreshLesson={fetchLesson} />
                </TabPanel>
                <TabPanel value={activeTab} index={1}>
                  <OpenQuestionAssignmentForm lessonId={lesson && lesson.id} refreshLesson={fetchLesson} />
                </TabPanel>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </>
  );
}
